import { useMemo, useState } from "react";
import { yerelAn } from "@koclukpanel/contracts";
import { denemeGirisiAc } from "./deneme-havuz-api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  DurumRozeti,
  Ikon,
  MetinAlani,
  OnayKutusu,
  Sekmeler,
  type UnkDurum,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  denemeAta,
  denemeAtamaIptal,
  denemeAtamalari,
  ogrenciListesi,
  type DenemeAtamaSatiri,
  type DenemeSatiri,
} from "./api";

/**
 * DENEME KATILIMI VE ATAMA — F-EXM-04a (A2, 05.10.2026)
 *
 * Önceden atama yoktu: deneme öğrencinin listesine ancak bir SONUÇ
 * yazılınca düşüyordu. Bu bölüm denemeyi öğrencilere atar ve kimin
 * katıldığını / kaçırdığını gösterir.
 *
 * Kaçırdı SUNUCUDA okuma anında hesaplanıyor (pencere kapandı + sonuç
 * yok); ekran kendi saatine göre karar vermiyor. Atama düğmeleri yalnız
 * `atayabilir` iken; karar yine sunucuda (tümü-ya-hiç kapsam kontrolü).
 */

/*
  UNİKOÇ durum dörtlüsü (tarama-1 §3.5): katıldı yeşil, kaçırdı kırmızı, bekliyor
  turuncu. Eski "uyarı/nötr" tonları yerine ortak DurumRozeti — renk ekrandan
  ekrana kaymasın; durum metinle de yazılı, yalnız renge dayanmıyor.
*/
const KOVA: Record<DenemeAtamaSatiri["kova"], { etiket: string; durum: UnkDurum }> = {
  KATILDI: { etiket: "Katıldı", durum: "tamamlandi" },
  KACIRDI: { etiket: "Kaçırdı", durum: "eksik" },
  BEKLIYOR: { etiket: "Bekliyor", durum: "bekliyor" },
};

type KovaSuzgeci = "tumu" | DenemeAtamaSatiri["kova"];

export function DenemeKatilim({
  deneme,
  atayabilir,
}: {
  deneme: DenemeSatiri;
  atayabilir: boolean;
}) {
  const qc = useQueryClient();
  const [arama, setArama] = useState("");
  const [kova, setKova] = useState<KovaSuzgeci>("tumu");
  const [atamaAcik, setAtamaAcik] = useState(false);
  const [acilacak, setAcilacak] = useState<{
    ogrenciId: string;
    eylem: "yeniden-ac" | "kilidi-ac";
  } | null>(null);
  const [gerekce, setGerekce] = useState("");
  const [bitis, setBitis] = useState("");
  const anahtar = ["deneme-atamalari", deneme.id] as const;

  const katilim = useQuery({ queryKey: anahtar, queryFn: () => denemeAtamalari(deneme.id) });

  const iptal = useMutation({
    mutationFn: (ogrenciId: string) => denemeAtamaIptal(deneme.id, ogrenciId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: anahtar });
      await qc.invalidateQueries({ queryKey: ["denemeler-tanim"] });
    },
  });

  const ac = useMutation({
    mutationFn: () => {
      const [gun, saat] = bitis.split("T");
      const kisiselBitis = gun && saat ? yerelAn(gun, saat)?.toISOString() : undefined;
      return denemeGirisiAc(deneme.id, acilacak!.ogrenciId, acilacak!.eylem, {
        gerekce,
        ...(acilacak!.eylem === "yeniden-ac" ? { kisiselBitis } : {}),
      });
    },
    onSuccess: async () => {
      setAcilacak(null);
      setGerekce("");
      setBitis("");
      await qc.invalidateQueries({ queryKey: anahtar });
    },
  });

  // Arama istemcide: liste zaten kapsamla süzülmüş ve tek denemeye ait.
  const satirlar = useMemo(() => {
    const k = arama.trim().toLocaleLowerCase("tr-TR");
    const tum = (katilim.data?.ogrenciler ?? []).filter((s) => kova === "tumu" || s.kova === kova);
    if (!k) return tum;
    return tum.filter((s) =>
      `${s.ad} ${s.soyad} ${s.ogrenciNo ?? ""}`.toLocaleLowerCase("tr-TR").includes(k),
    );
  }, [arama, kova, katilim.data]);

  const ozet = katilim.data?.ozet;
  const kapandi = new Date(deneme.bitis).getTime() <= Date.now();

  return (
    <section className="border-notr-200 flex flex-col gap-3 border-t pt-3" aria-label="Katılım">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-notr-800 text-sm font-medium">Katılım</h4>
        {atayabilir && !kapandi && !atamaAcik && (
          <Buton gorunum="ikincil" onClick={() => setAtamaAcik(true)}>
            Öğrenci ata
          </Buton>
        )}
      </div>

      {/* F-EXM-18/19 — eylemi sunucu seçer; arayüz yetki veya pencere kararı üretmez. */}
      {acilacak && (
        <div className="flex flex-col gap-2">
          {acilacak.eylem === "yeniden-ac" && (
            <Alan
              etiket="Kişisel bitiş (Türkiye saati)"
              type="datetime-local"
              value={bitis}
              onChange={(e) => setBitis(e.target.value)}
            />
          )}
          <MetinAlani
            etiket="Açma gerekçesi"
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
          />
          <Buton yukleniyor={ac.isPending} onClick={() => ac.mutate()}>
            Açmayı onayla
          </Buton>
          <Buton gorunum="sessiz" onClick={() => setAcilacak(null)}>
            Vazgeç
          </Buton>
          {ac.isError && (
            <p role="alert">
              {ac.error instanceof ApiHatasi ? ac.error.message : "Giriş açılamadı."}
            </p>
          )}
        </div>
      )}
      {ozet && (
        <p className="text-metin-koyu text-sm" aria-live="polite">
          {ozet.atanan} atandı ·{" "}
          <span className="inline-flex items-center gap-1 font-semibold">
            <span aria-hidden="true" className="bg-tamam-nokta size-2 rounded-full" />
            {ozet.katildi} katıldı
          </span>{" "}
          ·{" "}
          <span className="inline-flex items-center gap-1">
            <span aria-hidden="true" className="bg-eksik-nokta size-2 rounded-full" />
            {ozet.kacirdi} kaçırdı
          </span>{" "}
          ·{" "}
          <span className="inline-flex items-center gap-1">
            <span aria-hidden="true" className="bg-bekliyor-nokta size-2 rounded-full" />
            {ozet.bekliyor} bekliyor
          </span>
        </p>
      )}

      {atamaAcik && (
        <AtamaFormu
          deneme={deneme}
          atanmis={new Set((katilim.data?.ogrenciler ?? []).map((s) => s.ogrenciId))}
          onKapat={() => setAtamaAcik(false)}
          onAtandi={async () => {
            await qc.invalidateQueries({ queryKey: anahtar });
            await qc.invalidateQueries({ queryKey: ["denemeler-tanim"] });
          }}
        />
      )}

      {katilim.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {katilim.error instanceof ApiHatasi ? katilim.error.message : "Katılım alınamadı."}
        </p>
      )}

      {(katilim.data?.ogrenciler.length ?? 0) > 0 && (
        <>
          <Alan
            etiket="Öğrenci ara"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            yardim="Ad, soyad ya da öğrenci numarası."
          />
          <Sekmeler
            etiket="Katılım durumu"
            secili={kova}
            onSec={setKova}
            sekmeler={[
              { deger: "tumu", etiket: "Tümü", sayi: katilim.data?.ogrenciler.length ?? 0 },
              { deger: "KATILDI", etiket: "Katıldı", sayi: ozet?.katildi ?? 0 },
              { deger: "KACIRDI", etiket: "Kaçırdı", sayi: ozet?.kacirdi ?? 0 },
              { deger: "BEKLIYOR", etiket: "Bekliyor", sayi: ozet?.bekliyor ?? 0 },
            ]}
          />
          <ul className="flex flex-col gap-1">
            {satirlar.map((s) => {
              const eylem = (
                s as DenemeAtamaSatiri & { acmaEylemi?: "yeniden-ac" | "kilidi-ac" | null }
              ).acmaEylemi;
              return (
                <li
                  key={s.ogrenciId}
                  className="border-mor-kenar bg-notr-0 flex flex-wrap items-center gap-2 rounded-2xl border px-3 py-2 text-sm"
                >
                  <span
                    aria-hidden="true"
                    className="bg-mor-50 text-mor-500 grid size-8 shrink-0 place-items-center rounded-full"
                  >
                    <Ikon ad="profil" />
                  </span>
                  <span className="text-metin-koyu min-w-0 flex-1 truncate font-semibold">
                    {s.ad} {s.soyad}
                    {s.ogrenciNo != null && (
                      <span className="text-metin-ikincil font-normal"> · {s.ogrenciNo}</span>
                    )}
                  </span>
                  {s.net != null && (
                    <span className="text-notr-700">{Math.round(s.net * 100) / 100} net</span>
                  )}
                  <DurumRozeti durum={KOVA[s.kova].durum}>{KOVA[s.kova].etiket}</DurumRozeti>
                  {eylem && (
                    <Buton
                      gorunum="ikincil"
                      onClick={() => {
                        ac.reset();
                        setGerekce("");
                        setBitis("");
                        setAcilacak({ ogrenciId: s.ogrenciId, eylem });
                      }}
                    >
                      {eylem === "yeniden-ac" ? "Girişi yeniden aç" : "Düzeltmeye aç"}
                      <span className="sr-only">
                        {" "}
                        — {s.ad} {s.soyad}
                      </span>
                    </Buton>
                  )}
                  {atayabilir && s.iptalEdilebilir && !kapandi && (
                    <Buton
                      gorunum="sessiz"
                      yukleniyor={iptal.isPending && iptal.variables === s.ogrenciId}
                      onClick={() => iptal.mutate(s.ogrenciId)}
                    >
                      Geri al
                      <span className="sr-only">
                        {" "}
                        — {s.ad} {s.soyad}
                      </span>
                    </Buton>
                  )}
                </li>
              );
            })}
          </ul>
          {satirlar.length === 0 && <p className="text-notr-600 text-sm">Aramayla eşleşen yok.</p>}
        </>
      )}

      {katilim.data && katilim.data.ogrenciler.length === 0 && (
        <p className="text-notr-600 text-sm">Bu deneme henüz kimseye atanmadı.</p>
      )}

      {iptal.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {iptal.error instanceof ApiHatasi ? iptal.error.message : "Geri alınamadı."}
        </p>
      )}
    </section>
  );
}

function AtamaFormu({
  deneme,
  atanmis,
  onKapat,
  onAtandi,
}: {
  deneme: DenemeSatiri;
  atanmis: Set<string>;
  onKapat: () => void;
  onAtandi: () => Promise<void>;
}) {
  const [secili, setSecili] = useState<Set<string>>(new Set());
  const [arama, setArama] = useState("");
  const [sonuc, setSonuc] = useState<string | null>(null);
  const liste = useQuery({ queryKey: ["ogrenciler"], queryFn: ogrenciListesi });

  const ata = useMutation({
    mutationFn: (secim: { ogrenciIdler: string[] } | { kapsamimdakiHepsi: true }) =>
      denemeAta(deneme.id, secim),
    onSuccess: async (r) => {
      setSonuc(
        `${r.yeni + r.geriAcilan} öğrenciye atandı` +
          (r.zatenAtanmis > 0 ? ` (${r.zatenAtanmis} zaten atanmıştı).` : "."),
      );
      setSecili(new Set());
      await onAtandi();
    },
  });

  const adaylar = useMemo(() => {
    const k = arama.trim().toLocaleLowerCase("tr-TR");
    return (liste.data?.ogrenciler ?? []).filter(
      (o) =>
        !atanmis.has(o.id) &&
        (!k || `${o.ad} ${o.soyad} ${o.studentNo}`.toLocaleLowerCase("tr-TR").includes(k)),
    );
  }, [liste.data, atanmis, arama]);

  const degistir = (id: string) =>
    setSecili((s) => {
      const y = new Set(s);
      if (y.has(id)) y.delete(id);
      else y.add(id);
      return y;
    });

  return (
    <div className="rounded-kart border-notr-200 flex flex-col gap-3 border p-3">
      <Alan
        etiket="Atanacak öğrenciyi ara"
        value={arama}
        onChange={(e) => setArama(e.target.value)}
      />
      {liste.isLoading && <p className="text-notr-600 text-sm">Öğrenciler yükleniyor…</p>}
      <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
        {adaylar.map((o) => (
          <li key={o.id}>
            <OnayKutusu
              etiket={`${o.ad} ${o.soyad} · ${o.studentNo}`}
              checked={secili.has(o.id)}
              onChange={() => degistir(o.id)}
            />
          </li>
        ))}
      </ul>
      {liste.data && adaylar.length === 0 && (
        <p className="text-notr-600 text-sm">Atanabilecek başka öğrenci yok.</p>
      )}

      {ata.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {ata.error instanceof ApiHatasi ? ata.error.message : "Atama yapılamadı."}
        </p>
      )}
      {sonuc && (
        <p role="status" className="text-onay-700 text-sm font-medium">
          {sonuc}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Buton
          disabled={secili.size === 0}
          yukleniyor={ata.isPending}
          onClick={() => ata.mutate({ ogrenciIdler: [...secili] })}
        >
          Seçilenlere ata ({secili.size})
        </Buton>
        <Buton
          gorunum="ikincil"
          yukleniyor={ata.isPending}
          onClick={() => ata.mutate({ kapsamimdakiHepsi: true })}
        >
          Tüm öğrencilerime ata
        </Buton>
        <Buton gorunum="sessiz" onClick={onKapat}>
          Kapat
        </Buton>
      </div>
    </div>
  );
}
